import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod/v4';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { Lock } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useUpdateTtlockCredentials } from '@/api/hooks/usePlatformTenantDetail';
import type { AppError } from '@/api/client';
import { CredentialsStatusBadge } from './CredentialsStatusBadge';

const ttlockSchema = z.object({
  clientId: z.string().min(1, 'required'),
  clientSecret: z.string().min(1, 'required'),
  username: z.string().min(1, 'required'),
  passwordMd5: z
    .string()
    .regex(/^[a-f0-9]{32}$/i, 'md5_format'),
  apiBaseUrl: z.union([z.string().url(), z.literal('')]).optional(),
});

type TtlockForm = z.infer<typeof ttlockSchema>;

type Props = {
  tenantId: string;
  isConfigured: boolean;
};

export function TTLockCredentialsCard({ tenantId, isConfigured }: Props) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [serverError, setServerError] = useState<string | null>(null);
  const mutation = useUpdateTtlockCredentials(tenantId);

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting },
  } = useForm<TtlockForm>({
    resolver: zodResolver(ttlockSchema) as never,
    defaultValues: {
      clientId: '',
      clientSecret: '',
      username: '',
      passwordMd5: '',
      apiBaseUrl: '',
    },
  });

  const close = () => {
    setOpen(false);
    setServerError(null);
    reset();
  };

  const onSubmit = async (data: TtlockForm) => {
    setServerError(null);
    try {
      const result = await mutation.mutateAsync({
        clientId: data.clientId,
        clientSecret: data.clientSecret,
        username: data.username,
        passwordMd5: data.passwordMd5,
        apiBaseUrl: data.apiBaseUrl || undefined,
      });
      if (result.credentialsSet) {
        toast.success(t('platform_admin.tenant_detail.credentials.ttlock.save_success'));
        close();
      } else {
        // Server returned 200 + credentialsSet=false (defensive — shouldn't happen with current backend).
        setServerError(result.message);
      }
    } catch (err) {
      const appErr = err as AppError;
      setServerError(appErr.message ?? t('common.error'));
    }
  };

  const fieldError = (key: keyof TtlockForm) => {
    const e = errors[key];
    if (!e?.message) return null;
    return t(`platform_admin.tenant_detail.credentials.errors.${e.message}`);
  };

  return (
    <section className="rounded-xl border border-neutral-200 bg-white p-6 shadow-sm">
      <div className="flex items-start justify-between gap-4">
        <div className="flex items-start gap-3">
          <div className="rounded-lg bg-neutral-100 p-2">
            <Lock className="h-5 w-5 text-neutral-700" aria-hidden="true" />
          </div>
          <div>
            <h3 className="text-base font-semibold text-neutral-900">
              {t('platform_admin.tenant_detail.credentials.ttlock.title')}
            </h3>
            <p className="mt-1 text-sm text-neutral-500">
              {t('platform_admin.tenant_detail.credentials.ttlock.description')}
            </p>
          </div>
        </div>
        <CredentialsStatusBadge isConfigured={isConfigured} />
      </div>

      <div className="mt-4">
        <Button
          type="button"
          variant={isConfigured ? 'outline' : 'default'}
          onClick={() => setOpen(true)}
        >
          {isConfigured
            ? t('platform_admin.tenant_detail.credentials.update_button')
            : t('platform_admin.tenant_detail.credentials.ttlock.configure_button')}
        </Button>
      </div>

      <Dialog open={open} onOpenChange={(o) => (o ? setOpen(true) : close())}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>
              {t('platform_admin.tenant_detail.credentials.ttlock.modal_title')}
            </DialogTitle>
          </DialogHeader>

          <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
            <div className="space-y-1.5">
              <Label htmlFor="ttlock-clientId">
                {t('platform_admin.tenant_detail.credentials.ttlock.fields.client_id')}
              </Label>
              <Input id="ttlock-clientId" autoComplete="off" {...register('clientId')} />
              {fieldError('clientId') && (
                <p className="text-xs text-red-600">{fieldError('clientId')}</p>
              )}
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="ttlock-clientSecret">
                {t('platform_admin.tenant_detail.credentials.ttlock.fields.client_secret')}
              </Label>
              <Input
                id="ttlock-clientSecret"
                type="password"
                autoComplete="new-password"
                {...register('clientSecret')}
              />
              {fieldError('clientSecret') && (
                <p className="text-xs text-red-600">{fieldError('clientSecret')}</p>
              )}
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="ttlock-username">
                {t('platform_admin.tenant_detail.credentials.ttlock.fields.username')}
              </Label>
              <Input id="ttlock-username" autoComplete="off" {...register('username')} />
              {fieldError('username') && (
                <p className="text-xs text-red-600">{fieldError('username')}</p>
              )}
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="ttlock-passwordMd5">
                {t('platform_admin.tenant_detail.credentials.ttlock.fields.password_md5')}
              </Label>
              <Input
                id="ttlock-passwordMd5"
                type="password"
                autoComplete="new-password"
                placeholder="32-char hex"
                {...register('passwordMd5')}
              />
              <p className="text-[11px] text-neutral-400 font-mono">
                {t('platform_admin.tenant_detail.credentials.ttlock.fields.password_md5_hint')}
              </p>
              {fieldError('passwordMd5') && (
                <p className="text-xs text-red-600">{fieldError('passwordMd5')}</p>
              )}
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="ttlock-apiBaseUrl">
                {t('platform_admin.tenant_detail.credentials.ttlock.fields.api_base_url')}{' '}
                <span className="text-neutral-400">
                  ({t('platform_admin.tenant_detail.credentials.optional')})
                </span>
              </Label>
              <Input
                id="ttlock-apiBaseUrl"
                placeholder="https://euapi.ttlock.com"
                {...register('apiBaseUrl')}
              />
              {fieldError('apiBaseUrl') && (
                <p className="text-xs text-red-600">{fieldError('apiBaseUrl')}</p>
              )}
            </div>

            {serverError && (
              <div
                role="alert"
                className="rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-700"
              >
                {serverError}
              </div>
            )}

            <DialogFooter>
              <Button type="button" variant="outline" onClick={close}>
                {t('common.cancel')}
              </Button>
              <Button type="submit" disabled={isSubmitting}>
                {isSubmitting
                  ? t('platform_admin.tenant_detail.credentials.validating')
                  : t('platform_admin.tenant_detail.credentials.save')}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </section>
  );
}
